import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ControlContainer, FormGroupDirective, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CodeConversionMstDto, Sgke903aResponseCompanyAreaInfo, Sgke903aResponseInitialhyojiInfo } from 'sg-api';
import { CodeConversionMstService } from '../../../../../../sg-base/sg-base-shared/services/codeConversionMst.service';
import { SelectBox } from 'cbc-shared-lib';

@Component({
  selector: 'sgke300d-search-condition',
  templateUrl: './sgke300d-search-condition.component.html',
  styleUrls: ['./sgke300d-search-condition.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }]
})
export class Sgke300dSearchConditionComponent implements OnInit, OnDestroy {
  /** 会社地区情報 */
  @Input() cmpnyAreaList: Array<Sgke903aResponseCompanyAreaInfo>;
  /** 初期表示用会社地区情報（将来用） */
  @Input() cmpnyAreaInit: Sgke903aResponseInitialhyojiInfo;
  /** 検索済フラグ */
  @Input() searched: boolean;
  /** 業務日付 */
  @Input() businessDate: Date = null;

  /** クリアボタン押下イベント */
  @Output() onClickClearEvent = new EventEmitter();

  /** 購読を停止するためのSubject */
  private onDestroy$ = new Subject();

  /** 検索フォーム */
  searchConditionForm: UntypedFormGroup;

  /** 会社リスト */
  companyList: SelectBox[] = [];
  /** 地区リスト */
  chikuList: SelectBox[] = [];
  /** コード変換マスターマップ */
  codeMap: Map<string, CodeConversionMstDto[]>;

  /** 督促管理区分コード 画面項目IDコード値 */
  static S300090: string = "S300090";
  /** 料金区分コード 画面項目IDコード値 */
  static S300620: string = "S300620";
  /** 督促ステータスード 画面項目IDコード値 */
  static S301520: string = "S301520";

  // 督促管理区分コード コード値
  /** 無訪問 */
  static S300090_NOVISIT: String = "1";
  /** センター管理 */
  static S300090_CENTERMANAGE: String = "2";
  /** 営業統括管理 */
  static S300090_SALESMANAGE: String = "3";

  /**
   * コンストラクタ
   * @param parent 親FormGroup
   * @param fb FormBuilder
   * @param codeConvert コード変換マスター取得サービス
   */
  constructor(
    private parent: FormGroupDirective,
    private fb: UntypedFormBuilder,
    private codeConvert: CodeConversionMstService,
    private cb: ChangeDetectorRef,
  ) {
  }
  /**
   * コンポーネント初期化処理
   */
  ngOnInit(): void {
    this.searchConditionForm = this.fb.group({
      pressManagementdivisionCode: this.fb.nonNullable.control({
        value: [Sgke300dSearchConditionComponent.S300090_NOVISIT,
        Sgke300dSearchConditionComponent.S300090_CENTERMANAGE], disabled: false
      }, Validators.required),
      companycode: this.fb.nonNullable.control({ value: null, disabled: false }),
      areacode: this.fb.nonNullable.control({ value: null, disabled: false }),
      chargeDivcd: [{ value: null, disabled: false }],
      searchconditionPressStatuscode: [{ value: null, disabled: false }],
      koufuYearmonthdateStart: [{ value: null, disabled: false }],
      koufuYearmonthdateEnd: this.fb.nonNullable.control({ value: this.businessDate, disabled: false }),
    });

    // セレクターに値をセットする
    this.companyList = this.getSelector('companycode', 'companycodeAname', this.cmpnyAreaList);
    this.chikuList = this.getSelector('areacode', 'areacodeAname', this.cmpnyAreaList);

    // 親コンポーネントのformに検索条件formを追加
    this.parent.form.addControl("cndt", this.searchConditionForm);

    // コード変換マスタ取得
    let dispItemIdList = [
      Sgke300dSearchConditionComponent.S300090,
      Sgke300dSearchConditionComponent.S300620,
      Sgke300dSearchConditionComponent.S301520,
    ];
    // コード変換マスタ共通コンポーネント呼び出し
    this.codeConvert.getCodeConversionMst(dispItemIdList).pipe(takeUntil(this.onDestroy$)).subscribe(codeMap => {
      // ローカルに保存
      this.codeMap = codeMap;
    });

    this.cb.detectChanges();
  }
  /**
   * コンポーネント終了処理
   */
  ngOnDestroy(): void {
    this.searchConditionForm.clearValidators();
    this.searchConditionForm.updateValueAndValidity();
    this.searchConditionForm.removeControl('cndt');
    this.onDestroy$.next();
  }

  /** コントロール取得 */
  getControl(ctrlName): UntypedFormControl {
    return this.searchConditionForm.get(ctrlName) as UntypedFormControl;
  }

  /**
   * 会社ドロップダウン変化時処理
   * @param companycode 会社コード
   */
  onChangeCompany(companycode: string) {
    // 会社コードリストの値が変わった場合
    const chikuCtl = this.getControl('areacode') as UntypedFormControl;
    chikuCtl?.setValue(null);
    if (companycode) {
      const newAreaList: Array<Sgke903aResponseCompanyAreaInfo> = this.cmpnyAreaList
        .filter(list => list.companycode == companycode);
      this.chikuList = this.getSelector('areacode', 'areacodeAname', newAreaList);
    } else {
      this.chikuList = this.getSelector('areacode', 'areacodeAname', this.cmpnyAreaList);
    }
  }

  /**
   * クリアボタン押下時処理
   */
  onClickClear(): void {
    this.searchConditionForm.reset();
    this.onClickClearEvent.emit();
    this.onChangeCompany(null);
  }

  /**
  * SGKE903Aレスポンスからセレクターにオプションをセット
  * @private
  * @param {string} value
  * @param {string} viewValue
  * @return {SelectBox[]}
  * @memberof Sgke400dConditionComponent
  */
  private getSelector(value: string, viewValue: string, companyAreaInfoList: Array<Sgke903aResponseCompanyAreaInfo>): SelectBox[] {

    // SGKE903Aレスポンスのコードとコード値をまとめてリスト化
    let retSelectBoxList: SelectBox[] = [];
    companyAreaInfoList.map(data => {
      const select: SelectBox = { value: data[value], viewValue: data[viewValue] };
        retSelectBoxList.push(select);
    });
      // 重複を削除しソート
    const uniqueSortedArray = Array.from(
      new Map(retSelectBoxList.map(item => [item.value, item])).values()
    ).sort((a, b) => a.value > b.value ? 1 : -1);
    return uniqueSortedArray;
  }
}
